import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2, Lock, LogOut, Plus, Pencil, Trash2, Save, X,
  Search, Wallet, Users, Car, Printer, IndianRupee, Filter, Copy, CalendarCheck,
  CheckCircle2, Circle, ShieldCheck, Unlock, TrainFront, Check, Clock,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_ABBR = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const periodLabel = (p) => {
  const [mm, yy] = String(p || '').split('-');
  return `${MONTH_ABBR[Number(mm)] || mm} ${String(yy || '').slice(-2)}`;
};

// "9 Oct, 3:42 PM" from an ISO timestamp
const fmtStamp = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

// Download the approved-month payroll PDF for one entity (auth via axios → blob)
const downloadPayrollPdf = async (period, entity) => {
  try {
    const res = await axios.get(`${API}/admin/payroll/export`, {
      params: { pay_period: period, entity }, responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${entity.replace(/[^a-z0-9]+/gi, '_')}_${period}_Payroll.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  } catch (e) {
    window.alert('Download failed — make sure the month is Partner-approved.');
  }
};

const PAYMENT_MODES = ['RTGS/NEFT', 'Cheque', 'Cash'];
const ALLOWANCE_FIELDS = [
  ['hra',     'HRA'],
  ['da',      'DA'],
  ['travel',  'Travel'],
  ['medical', 'Medical'],
  ['other',   'Other'],
];
const DEDUCTION_FIELDS = [
  ['pf',                'PF'],
  ['esi',               'ESI'],
  ['professional_tax',  'Prof. Tax'],
  ['income_tax',        'Income Tax'],
  ['other',             'Other'],
];

const MONTHS = [
  ['01', 'Jan'], ['02', 'Feb'], ['03', 'Mar'], ['04', 'Apr'],
  ['05', 'May'], ['06', 'Jun'], ['07', 'Jul'], ['08', 'Aug'],
  ['09', 'Sep'], ['10', 'Oct'], ['11', 'Nov'], ['12', 'Dec'],
];

const nowPeriod = () => {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

const money = (n) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(Number(n) || 0));

// Normalise entity labels. Blank / "RST & Co" / "RST & Co." all collapse to the
// canonical "RST & Co." so RST rows group together regardless of how they were saved.
const normEntity = (e) => {
  const v = String(e || '').trim();
  if (!v) return 'RST & Co.';
  if (/^rst\s*&\s*co\.?$/i.test(v)) return 'RST & Co.';
  return v;
};
// Entity display order: SCCS LLP, then RST & Co., then everything else alphabetically.
const entityRank = (e) => (e === 'SCCS LLP' ? 0 : e === 'RST & Co.' ? 1 : 2);

// Designation seniority — lower number = more senior (shown higher in a team).
// Checked in order, first match wins, so put specific patterns before generic ones.
const DESIG_RANK_PATTERNS = [
  /partner/i,
  /director|principal/i,
  /manager/i,
  /(audit\s*)?executive/i,
  /senior\s*account/i,
  /account/i,
  /senior.*articled?/i,
  /articled?|trainee|intern/i,
  /receptionist/i,
  /attendant|office\s*assistant|peon|assistant|support|staff/i,
];
const desigRank = (d) => {
  const s = d || '';
  for (let i = 0; i < DESIG_RANK_PATTERNS.length; i++) if (DESIG_RANK_PATTERNS[i].test(s)) return i;
  return DESIG_RANK_PATTERNS.length; // unknown designations fall to the bottom
};

const formatError = (detail) => {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

const toISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return '';
};
const fromISODate = (s) => {
  if (!s) return '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};

const emptyEntry = () => ({
  employee_id: '',
  employee_name: '',
  designation: '',
  entity: '',
  department: '',
  pay_period: nowPeriod(),
  basic_pay: 0,
  allowances: Object.fromEntries(ALLOWANCE_FIELDS.map(([k]) => [k, 0])),
  deductions: { ...Object.fromEntries(DEDUCTION_FIELDS.map(([k]) => [k, 0])), salary_advance: 0 },
  advance_amount: 0,
  advance_date: '',
  advance_emi_count: 0,
  advance_balance: 0,
  working_days: 0,
  present_days: 0,
  absent_days: 0,
  payment_mode: 'RTGS/NEFT',
  payment_ref: '',
  payment_date: '',
  remarks: '',
});

const FieldGroup = ({ label, children, className = '' }) => (
  <div className={className}>
    <label className="block text-[10px] font-mono tracking-widest text-gray-600 uppercase mb-1.5">
      {label}
    </label>
    {children}
  </div>
);

const PayrollPage = () => {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();

  const [rows, setRows]         = useState([]);
  const [employees, setEmps]    = useState([]);
  const [monthly, setMonthly]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  const [period, setPeriod]         = useState(nowPeriod());
  const [dept, setDept]             = useState('');
  const [q, setQ]                   = useState('');
  const [modeFilter, setModeFilter] = useState('');

  const [editing, setEditing] = useState(null);
  const [saving, setSaving]   = useState(false);
  const [approval, setApproval] = useState(null);   // month-level approval + stage progress
  const [acting, setActing]   = useState(false);    // a toggle / approve call is in flight
  const printRef = useRef(null);

  useEffect(() => {
    if (ready && !user) navigate('/admin/login?next=/admin/payroll', { replace: true });
  }, [ready, user, navigate]);

  const loadApproval = async (p = period) => {
    try {
      const { data } = await axios.get(`${API}/admin/payroll/approval`, { params: { pay_period: p } });
      setApproval(data);
    } catch { setApproval(null); }
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const params = {};
      if (period) params.pay_period = period;
      if (dept)   params.department  = dept;
      const [{ data: p }, { data: emps }] = await Promise.all([
        axios.get(`${API}/admin/payroll`, { params }),
        axios.get(`${API}/admin/employees?limit=500`),
      ]);
      setRows(Array.isArray(p) ? p : []);
      setEmps(Array.isArray(emps) ? emps : []);
      loadApproval(period);
      try {
        const { data: ms } = await axios.get(`${API}/admin/payroll/monthly-summary`, { params: { months: 12 } });
        setMonthly(Array.isArray(ms) ? ms : []);
      } catch { /* trend chart is supplementary */ }
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (ready && user) load(); /* eslint-disable-next-line */ }, [ready, user, period, dept]);

  const locked = !!approval?.approved;

  // Per-employee Initiate (accountant) toggle
  const toggleInitiate = async (r) => {
    if (acting) return;
    setActing(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/payroll/${r.id}/initiate`, { value: !r.initiated_at });
      setRows((prev) => prev.map((x) => (x.id === r.id ? data : x)));
      await loadApproval(period);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Initiate failed');
    } finally { setActing(false); }
  };

  // Per-employee Verify (manager) toggle
  const toggleVerify = async (r) => {
    if (acting) return;
    setActing(true); setError('');
    try {
      const { data } = await axios.post(`${API}/admin/payroll/${r.id}/verify`, { value: !r.verified_at });
      setRows((prev) => prev.map((x) => (x.id === r.id ? data : x)));
      await loadApproval(period);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Verify failed');
    } finally { setActing(false); }
  };

  // Partner approves the whole month (locks it)
  const approveMonth = async () => {
    if (acting || !approval?.can_approve) return;
    if (!window.confirm(`Partner approval for ${period}?\n\nThis locks the month — no further edits, attendance or stage changes until unlocked.`)) return;
    setActing(true); setError('');
    try {
      await axios.post(`${API}/admin/payroll/approve`, { pay_period: period });
      await loadApproval(period);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Approval failed');
    } finally { setActing(false); }
  };

  const unapproveMonth = async () => {
    if (acting) return;
    if (!window.confirm(`Unlock ${period}? This revokes the Partner approval and re-opens the month for edits.`)) return;
    setActing(true); setError('');
    try {
      await axios.post(`${API}/admin/payroll/unapprove`, { pay_period: period });
      await loadApproval(period);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Unlock failed');
    } finally { setActing(false); }
  };


  // Same division taxonomy as Team page:
  //   • Firm Leadership · Head Partners
  //   • Team of <Partner Name>            (one per active partner)
  //   • Office Assistants · Receptionists · Attendants
  //   • Unassigned to Partner
  const LEADERSHIP_DIVISION = 'Firm Leadership · Head Partners';
  const OFFICE_DIVISION     = 'Office Assistants · Receptionists · Attendants';
  const UNASSIGNED_DIVISION = 'Unassigned to Partner';

  const isPartnerEmp     = (e) => /partner/i.test(e.designation || '') && !/associate/i.test(e.designation || '');
  const isOfficeStaffEmp = (e) => /office\s*assistant|attendant|receptionist/i.test(e.designation || '');

  // Payroll-row partner test (rows carry department + designation).
  const isPartnerRow = (r) =>
    r.department === LEADERSHIP_DIVISION ||
    (/partner/i.test(r.designation || '') && !/associate/i.test(r.designation || ''));
  // RST & Co. partners are NOT on a monthly salary → excluded from payroll entirely.
  const isRstPartnerRow = (r) => isPartnerRow(r) && normEntity(r.entity) === 'RST & Co.';

  const partners = useMemo(
    () => employees.filter((e) => isPartnerEmp(e) && !e.date_of_leaving),
    [employees]
  );

  const partnerById = useMemo(() => {
    const m = new Map();
    partners.forEach((p) => m.set(p.id, p));
    return m;
  }, [partners]);

  const divisionForEmployee = (emp) => {
    if (!emp) return '';
    if (isPartnerEmp(emp))     return LEADERSHIP_DIVISION;
    if (isOfficeStaffEmp(emp)) return OFFICE_DIVISION;
    const p = emp.reports_to_partner_id ? partnerById.get(emp.reports_to_partner_id) : null;
    return p ? `Team of ${p.name}` : UNASSIGNED_DIVISION;
  };

  // Division list — same order as Team page. Only includes buckets that
  // actually have members among active employees, plus any legacy value
  // already present on existing payroll rows (so historical entries still
  // filter correctly).
  const divisions = useMemo(() => {
    const active = employees.filter((e) => !e.date_of_leaving);
    const usedTeams = new Set();
    let hasLeadership = false, hasOffice = false, hasUnassigned = false;
    for (const e of active) {
      if (isPartnerEmp(e))          hasLeadership = true;
      else if (isOfficeStaffEmp(e)) hasOffice = true;
      else if (e.reports_to_partner_id && partnerById.has(e.reports_to_partner_id)) {
        usedTeams.add(partnerById.get(e.reports_to_partner_id).name);
      } else hasUnassigned = true;
    }
    const ordered = [];
    if (hasLeadership) ordered.push(LEADERSHIP_DIVISION);
    partners
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((p) => { if (usedTeams.has(p.name)) ordered.push(`Team of ${p.name}`); });
    if (hasOffice)     ordered.push(OFFICE_DIVISION);
    if (hasUnassigned) ordered.push(UNASSIGNED_DIVISION);
    // Also surface any legacy divisions saved on existing rows
    rows.forEach((r) => { if (r.department && !ordered.includes(r.department)) ordered.push(r.department); });
    return ordered;
  }, [employees, partners, partnerById, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeEmployees = useMemo(
    () => employees.filter((e) => !e.date_of_leaving).sort((a, b) => a.name.localeCompare(b.name)),
    [employees]
  );

  const [syncing, setSyncing] = useState(false);
  const normMobile = (v) => String(v || '').replace(/\D/g, '').slice(-10);

  // Inline attendance update → recomputes leave deduction + net on the server
  const patchAttendance = async (rowId, vals) => {
    try {
      const { data } = await axios.patch(`${API}/admin/payroll/${rowId}/attendance`, {
        working_days: Number(vals.working_days) || 0,
        present_days: Number(vals.present_days) || 0,
        absent_days: Number(vals.absent_days) || 0,
      });
      setRows((prev) => prev.map((r) => (r.id === rowId ? data : r)));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Attendance update failed');
    }
  };

  // Inline payment-mode change from the register row
  const patchPaymentMode = async (r, mode) => {
    if (acting) return;
    setError('');
    try {
      const { data } = await axios.patch(`${API}/admin/payroll/${r.id}/payment-mode`, { payment_mode: mode });
      setRows((prev) => prev.map((x) => (x.id === r.id ? data : x)));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Payment mode update failed');
    }
  };

  // Pull Jamku attendance for the current pay period and apply to matching rows
  const syncJamku = async () => {
    if (!window.confirm(`Fetch attendance from Jamku for ${period} and update Working/Present/Absent for matching employees?`)) return;
    setSyncing(true); setError('');
    try {
      const { data } = await axios.get(`${API}/admin/payroll/jamku-attendance`, { params: { period } });
      const byMobile = {}, byUser = {};
      Object.values(data.by_key || {}).forEach((v) => {
        if (v.mobile) byMobile[v.mobile] = v;
        if (v.username) byUser[v.username.toLowerCase().replace(/\s/g, '')] = v;
      });
      let matched = 0; const unmatched = [];
      for (const r of rows.filter((x) => x.pay_period === period)) {
        const emp = employees.find((e) => e.name?.toLowerCase() === r.employee_name?.toLowerCase());
        const mob = emp ? normMobile(emp.contact) : '';
        let att = (mob && byMobile[mob]) || byUser[(r.employee_name || '').toLowerCase().replace(/\s/g, '')];
        if (att) {
          matched++;
          await patchAttendance(r.id, { working_days: att.working, present_days: att.present, absent_days: att.absent });
        } else {
          unmatched.push(r.employee_name);
        }
      }
      await load();
      alert(`Jamku sync complete for ${period}.\nUpdated ${matched} employee(s).`
        + (unmatched.length ? `\n\nNo attendance match for: ${unmatched.join(', ')}` : ''));
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Jamku sync failed');
    } finally { setSyncing(false); }
  };

  // Distinct entities from active employees + any legacy values on existing rows
  const allEntities = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => { if (e.entity) set.add(String(e.entity).trim()); });
    rows.forEach((r) => { if (r.entity) set.add(String(r.entity).trim()); });
    return Array.from(set).filter(Boolean).sort();
  }, [employees, rows]);

  // Available years — last 5 + current
  const years = useMemo(() => {
    const cur = new Date().getFullYear();
    return Array.from({ length: 6 }, (_, i) => cur - 4 + i);
  }, []);
  const [pMonth, pYear] = (period || '01-2026').split('-');

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return rows.filter((r) => {
      if (isRstPartnerRow(r)) return false;   // RST & Co. partners are not monthly-paid
      if (modeFilter && r.payment_mode !== modeFilter) return false;
      if (needle) {
        const hay = `${r.employee_name} ${r.designation || ''} ${r.department || ''} ${r.remarks || ''}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [rows, q, modeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => {
    let gross = 0, net = 0, ded = 0;
    for (const r of filtered) {
      gross += Number(r.gross_earnings) || 0;
      net   += Number(r.net_pay) || 0;
      ded   += (Number(r.gross_earnings) || 0) - (Number(r.net_pay) || 0);
    }
    return { count: filtered.length, gross, ded, net };
  }, [filtered]);

  // Entity-first grouping. Top level = entity (SCCS LLP, then RST & Co., then
  // others). Office Assistants / Attendants / Receptionists are treated as part
  // of CA Bharath Rao K S's team (they report to him), so they merge into his
  // "Team of …" sub-section instead of a standalone Office bucket. Within every
  // sub-section, members are ordered by designation seniority (senior → junior),
  // which naturally drops the office staff to the bottom of the team.
  const entityBlocks = useMemo(() => {
    const bharath = partners.find((p) => /bharath/i.test(p.name || ''));
    const OFFICE_TO_TEAM = bharath ? `Team of ${bharath.name}` : 'Team of CA Bharath Rao K S';

    const totals = (list) => {
      const g = list.reduce((a, r) => a + (Number(r.gross_earnings) || 0), 0);
      const n = list.reduce((a, r) => a + (Number(r.net_pay) || 0), 0);
      const b = list.reduce((a, r) => a + (Number(r.basic_pay) || 0), 0);
      return { gross: g, net: n, ded: g - n, basic: b, count: list.length };
    };
    // Effective sub-division for a row: office staff fold into Bharath's team.
    const rowDept = (r) => (r.department === OFFICE_DIVISION ? OFFICE_TO_TEAM : r.department);
    const subRank = (dept) => {
      if (dept === LEADERSHIP_DIVISION) return 0;
      if (/^Team of /.test(dept || '')) return 1;
      if (dept === UNASSIGNED_DIVISION) return 2;
      return 3;
    };
    const bySeniority = (a, b) => {
      const ra = desigRank(a.designation), rb = desigRank(b.designation);
      if (ra !== rb) return ra - rb;
      return (a.employee_name || '').localeCompare(b.employee_name || '');
    };

    const byEntity = new Map();
    const others = [];
    for (const r of filtered) {
      if (!r.department || !divisions.includes(r.department)) { others.push(r); continue; }
      const ent = normEntity(r.entity);
      if (!byEntity.has(ent)) byEntity.set(ent, []);
      byEntity.get(ent).push(r);
    }

    const buildBlock = (entity, list, extra = {}) => {
      const dmap = new Map();
      for (const r of list) {
        const d = rowDept(r) || '—';
        if (!dmap.has(d)) dmap.set(d, []);
        dmap.get(d).push(r);
      }
      const subs = Array.from(dmap.entries())
        .map(([title, srows]) => ({ title, rows: [...srows].sort(bySeniority), ...totals(srows) }))
        .sort((a, b) => {
          const ra = subRank(a.title), rb = subRank(b.title);
          return ra !== rb ? ra - rb : a.title.localeCompare(b.title);
        });
      return { entity, rows: list, subs, ...totals(list), ...extra };
    };

    const blocks = Array.from(byEntity.entries())
      .map(([e, list]) => buildBlock(e, list))
      .sort((a, b) => {
        const ra = entityRank(a.entity), rb = entityRank(b.entity);
        return ra !== rb ? ra - rb : a.entity.localeCompare(b.entity);
      });

    if (others.length) {
      blocks.push(buildBlock('Others / Ad-hoc', others, { isOthers: true }));
    }
    return blocks;
  }, [filtered, divisions, partners]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    const e = emptyEntry();
    e.pay_period = period;
    setEditing(e);
    setError('');
  };

  const [seeding, setSeeding] = useState(false);
  const bulkSeed = async () => {
    if (!window.confirm(`Import every active team member into payroll for ${period}?\n\nCreates a draft ₹0 entry for each employee that doesn't already have one for this period.`)) return;
    setSeeding(true); setError('');
    try {
      const form = new FormData();
      form.append('pay_period', period);
      const { data } = await axios.post(`${API}/admin/payroll/bulk-seed`, form);
      await load();
      alert(`Imported ${data.created} of ${data.total_active_employees} team members into ${period}.\n(${data.skipped_existing} already existed and were skipped.)`);
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Bulk import failed');
    } finally { setSeeding(false); }
  };

  const [copying, setCopying] = useState(false);
  const prevPeriod = useMemo(() => {
    const [m, y] = (period || '01-2026').split('-').map(Number);
    return m === 1 ? `12-${y - 1}` : `${String(m - 1).padStart(2, '0')}-${y}`;
  }, [period]);

  const copyFromPrevious = async () => {
    if (!window.confirm(
      `Copy every payroll entry from ${prevPeriod} into ${period}?\n\n` +
      `Amounts, allowances and deductions carry over — payment date and remarks are cleared. ` +
      `Rows that already exist in ${period} are left untouched.`
    )) return;
    setCopying(true); setError('');
    try {
      const form = new FormData();
      form.append('pay_period', period);
      const { data } = await axios.post(`${API}/admin/payroll/copy-from-previous`, form);
      await load();
      if (data.source_count === 0) {
        alert(`No payroll entries found for ${data.source_period} — nothing to copy.`);
      } else {
        alert(
          `Copied ${data.created} of ${data.source_count} entries from ${data.source_period} into ${data.pay_period}.\n` +
          `(${data.skipped_existing} already existed and were skipped.)`
        );
      }
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Copy failed');
    } finally { setCopying(false); }
  };

  const openEdit = (r) => {
    setEditing({
      ...emptyEntry(),
      ...r,
      allowances: { ...emptyEntry().allowances, ...(r.allowances || {}) },
      deductions: { ...emptyEntry().deductions, ...(r.deductions || {}) },
    });
    setError('');
  };
  const cancel = () => { setEditing(null); setError(''); };

  const handleField = (k, v) => setEditing((e) => ({ ...e, [k]: v }));
  const handleAllowance = (k, v) => setEditing((e) => ({ ...e, allowances: { ...e.allowances, [k]: v } }));
  const handleDeduction = (k, v) => setEditing((e) => ({ ...e, deductions: { ...e.deductions, [k]: v } }));

  // Salary advance split into EMIs: monthly deduction = advance / No. of EMI.
  // Recompute the monthly Deduction and remaining Balance whenever the advance
  // amount or EMI count changes. Balance is clamped at zero.
  const applyAdvance = (amount, count) => {
    const amt = Number(amount) || 0;
    const n = Number(count) || 0;
    setEditing((e) => {
      if (n > 0 && amt > 0) {
        const emi = Math.round(amt / n);
        return {
          ...e,
          advance_amount: amount,
          advance_emi_count: count,
          deductions: { ...e.deductions, salary_advance: emi },
          advance_balance: Math.max(0, amt - emi),
        };
      }
      return { ...e, advance_amount: amount, advance_emi_count: count };
    });
  };

  const onNameChange = (name) => {
    // Try match against active employees (case-insensitive) so designation +
    // division auto-fill when the user picks from the datalist. Free typing
    // is still allowed for ad-hoc entries — we just clear the linked ID.
    const trimmed = (name || '').trim();
    const match = trimmed
      ? activeEmployees.find((e) => e.name.toLowerCase() === trimmed.toLowerCase())
      : null;
    setEditing((e) => ({
      ...e,
      employee_name: name,
      employee_id: match ? match.id : '',
      designation: match ? (match.designation || e.designation) : e.designation,
      entity:      match ? (match.entity      || e.entity)      : e.entity,
      department:  match ? divisionForEmployee(match)           : e.department,
    }));
  };

  // Live duplicate detection — same party can't be in the same pay period twice
  const dupExisting = useMemo(() => {
    if (!editing) return null;
    const name = (editing.employee_name || '').trim().toLowerCase();
    const period = editing.pay_period;
    if (!name || !period) return null;
    return rows.find((r) => {
      if (editing.id && r.id === editing.id) return false;      // editing itself
      if (r.pay_period !== period) return false;
      if (editing.employee_id && r.employee_id === editing.employee_id) return true;
      if (!editing.employee_id && (r.employee_name || '').trim().toLowerCase() === name) return true;
      return false;
    }) || null;
  }, [editing, rows]);

  // Live totals in editor
  // Leave-salary deduction preview: absent × (gross ÷ calendar days in month)
  const leaveDeduction = useMemo(() => {
    if (!editing) return 0;
    const basic = Number(editing.basic_pay) || 0;
    const aSum  = Object.values(editing.allowances || {}).reduce((a, v) => a + (Number(v) || 0), 0);
    const gross = basic + aSum;
    const absent = Number(editing.absent_days) || 0;
    const [mm, yyyy] = (editing.pay_period || '01-2026').split('-').map(Number);
    const days = (mm && yyyy) ? new Date(yyyy, mm, 0).getDate() : 30;
    return days > 0 ? Math.round(absent * (gross / days)) : 0;
  }, [editing]);

  const editorTotals = useMemo(() => {
    if (!editing) return { gross: 0, ded: 0, net: 0, daily: 0 };
    const basic = Number(editing.basic_pay) || 0;
    const aSum  = Object.values(editing.allowances || {}).reduce((a, v) => a + (Number(v) || 0), 0);
    // sum all deductions EXCEPT the (stale) stored leave — use the live preview instead
    const dSum  = Object.entries(editing.deductions || {})
      .filter(([k]) => k !== 'leave')
      .reduce((a, [, v]) => a + (Number(v) || 0), 0) + leaveDeduction;
    const gross = basic + aSum;
    const [mm, yyyy] = (editing.pay_period || '01-2026').split('-').map(Number);
    const days = (mm && yyyy) ? new Date(yyyy, mm, 0).getDate() : 30;
    return { gross, ded: dSum, net: gross - dSum, daily: days > 0 ? Math.round(gross / days) : 0 };
  }, [editing, leaveDeduction]);

  const save = async () => {
    if (!editing.employee_name?.trim()) { setError('Employee Name is required'); return; }
    if (!/^(0[1-9]|1[0-2])-\d{4}$/.test(editing.pay_period || '')) {
      setError('Pay Period must be MM-YYYY (e.g., 01-2026)'); return;
    }
    if (dupExisting) {
      setError(`${dupExisting.employee_name} already has a payroll entry for ${dupExisting.pay_period}.`);
      return;
    }
    setSaving(true); setError('');
    try {
      const payload = {
        employee_id: editing.employee_id || null,
        employee_name: editing.employee_name.trim(),
        designation: editing.designation || null,
        entity: (editing.entity || '').trim() || null,
        department: editing.department || null,
        pay_period: editing.pay_period,
        basic_pay: Number(editing.basic_pay) || 0,
        allowances: Object.fromEntries(ALLOWANCE_FIELDS.map(([k]) => [k, Number(editing.allowances[k]) || 0])),
        deductions: {
          ...Object.fromEntries(DEDUCTION_FIELDS.map(([k]) => [k, Number(editing.deductions[k]) || 0])),
          salary_advance: Number(editing.deductions?.salary_advance) || 0,
        },
        advance_amount: Number(editing.advance_amount) || 0,
        advance_date: editing.advance_date || null,
        advance_emi_count: Number(editing.advance_emi_count) || 0,
        advance_balance: Number(editing.advance_balance) || 0,
        working_days: Number(editing.working_days) || 0,
        present_days: Number(editing.present_days) || 0,
        absent_days: Number(editing.absent_days) || 0,
        payment_mode: editing.payment_mode || 'RTGS/NEFT',
        payment_ref: (editing.payment_ref || '').trim() || null,
        payment_date: editing.payment_date || null,
        remarks: editing.remarks || null,
      };
      if (editing.id) await axios.put(`${API}/admin/payroll/${editing.id}`, payload);
      else            await axios.post(`${API}/admin/payroll`, payload);
      setEditing(null);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail) || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete payroll entry for ${r.employee_name} (${r.pay_period})?`)) return;
    try {
      await axios.delete(`${API}/admin/payroll/${r.id}`);
      await load();
    } catch (err) {
      setError(formatError(err?.response?.data?.detail));
    }
  };

  const doPrint = () => window.print();

  if (!ready || !user) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center px-4">
        <Loader2 className="text-brand-yellow animate-spin" size={22} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="payroll-page">
      {/* Admin bar */}
      <div className="bg-[#0a0f1e] border-b border-white/10 text-white sticky top-0 z-30 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[5%] py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/10 flex items-center justify-center">
              <Lock size={14} className="text-brand-yellow" />
            </div>
            <div>
              <div className="font-mono text-[9px] tracking-[0.2em] text-brand-yellow uppercase">
                Admin Dashboard
              </div>
              <div className="text-sm font-medium">
                Payroll · {stats.count} entries · {period}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={openNew}
              disabled={locked}
              className="bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 rounded-none h-9 px-4 text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="add-payroll-btn"
            >
              <Plus size={16} className="mr-1.5" /> Add Entry
            </Button>
            <button
              onClick={bulkSeed}
              disabled={seeding || locked}
              title={`Import all active team members into ${period}`}
              className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="import-team-btn"
            >
              {seeding ? <Loader2 size={13} className="animate-spin" /> : <Users size={13} />}
              Import Team
            </button>
            <button onClick={doPrint} className="inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="print-payroll-btn">
              <Printer size={13} /> Print
            </button>
            <button onClick={() => navigate('/team')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-team-btn">
              <Users size={13} /> Team
            </button>
            <button onClick={() => navigate('/admin/cashflow')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-cashflow-btn">
              <Wallet size={13} /> Cashflow
            </button>
            <button onClick={() => navigate('/admin/vehicles')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-vehicles-btn">
              <Car size={13} /> Vehicles
            </button>
            <button onClick={() => navigate('/admin/insurances')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-insurances-btn">
              <span aria-hidden>🛡</span> Insurances
            </button>
            <button onClick={() => navigate('/admin/policies')} className="hidden md:inline-flex items-center gap-1.5 text-white/70 hover:text-brand-yellow text-xs border border-white/15 hover:border-brand-yellow px-3 py-2 transition-colors" data-testid="nav-policies-btn">
              <span aria-hidden>📘</span> Policies
            </button>
            <span className="hidden md:block text-xs text-white/50 border-l border-white/10 pl-3">{user.email}</span>
            <button
              onClick={async () => { await logout(); navigate('/'); }}
              className="inline-flex items-center gap-1.5 bg-brand-yellow hover:bg-brand-yellow/90 text-gray-900 text-xs font-semibold transition-colors px-3 py-2"
              data-testid="payroll-logout-btn"
            >
              <LogOut size={13} /> Log out
            </button>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative bg-[#0a0f1e] pt-12 pb-14 overflow-hidden print:hidden">
        <div className="absolute inset-0 opacity-[0.10] bg-[linear-gradient(to_right,#ffffff18_1px,transparent_1px),linear-gradient(to_bottom,#ffffff18_1px,transparent_1px)] bg-[size:48px_48px] pointer-events-none" />
        <div className="absolute top-0 right-0 w-[600px] h-[500px] bg-brand-yellow/15 blur-[160px] rounded-full pointer-events-none" />
        <div className="relative max-w-[1720px] mx-auto px-[5%]">
          <div className="inline-flex items-center gap-2 border border-brand-yellow/40 bg-brand-yellow/10 px-3 py-1.5 mb-4">
            <IndianRupee size={13} className="text-brand-yellow" />
            <span className="font-mono text-[10px] tracking-[0.25em] text-brand-yellow uppercase">
              Internal · R S T &amp; Co.
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-black text-white leading-[1.02] tracking-tight">
            Payroll Register
          </h1>
          <p className="text-sm md:text-base text-white/60 mt-3 max-w-2xl">
            Monthly employee earnings, deductions and net pay. Filter by pay period, department or payment mode.
          </p>

          {/* KPIs (stacked) + monthly trend chart */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-8">
            {/* Left: 4 KPIs one below the other */}
            <div className="flex flex-col gap-px bg-white/10 border border-white/10">
              <KPI label="Entries"  value={String(stats.count)} icon={<Filter size={14} />} tone="mono" />
              <KPI label="Gross"    value={`₹${money(stats.gross)}`} icon={<IndianRupee size={14} />} tone="yellow" />
              <KPI label="Deductions" value={`₹${money(stats.ded)}`} icon={<IndianRupee size={14} />} tone="amber" />
              <KPI label="Net Pay"  value={`₹${money(stats.net)}`} icon={<IndianRupee size={14} />} tone="green" />
            </div>

            {/* Right: single chart with Gross/Deductions bars + Net line */}
            <div className="lg:col-span-2 bg-[#0a0f1e] border border-white/10 p-4" data-testid="payroll-trend-chart">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-widest uppercase text-white/60">
                  Monthly Trend · Gross · Deductions · Net · Employees
                </span>
                <span className="font-mono text-[9px] tracking-widest uppercase text-white/30">
                  last {monthly.length} month{monthly.length === 1 ? '' : 's'}
                </span>
              </div>
              {monthly.length === 0 ? (
                <div className="h-[220px] flex items-center justify-center text-white/40 text-sm">
                  No payroll data yet to chart.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={monthly.map((m) => ({ ...m, label: periodLabel(m.period) }))} margin={{ top: 6, right: 8, left: 8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff14" vertical={false} />
                    <XAxis dataKey="label" tick={{ fill: '#ffffff99', fontSize: 11 }} axisLine={{ stroke: '#ffffff22' }} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fill: '#ffffff99', fontSize: 11 }} axisLine={{ stroke: '#ffffff22' }} tickLine={false}
                      tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(1)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fill: '#60A5FA', fontSize: 11 }} axisLine={{ stroke: '#ffffff22' }} tickLine={false} allowDecimals={false} width={32} />
                    <Tooltip
                      contentStyle={{ background: '#0a0f1e', border: '1px solid #ffffff22', borderRadius: 0, color: '#fff', fontSize: 12 }}
                      formatter={(v, n) => [n === 'Employees' ? v : `₹${money(v)}`, n]}
                      cursor={{ fill: '#ffffff0a' }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: '#fff' }} iconType="plainline" />
                    <Bar yAxisId="left" dataKey="gross" name="Gross" fill="#FFC32B" barSize={14} radius={[2, 2, 0, 0]} />
                    <Bar yAxisId="left" dataKey="deductions" name="Deductions" fill="#F87171" barSize={14} radius={[2, 2, 0, 0]} />
                    <Line yAxisId="left" type="monotone" dataKey="net" name="Net" stroke="#4ADE80" strokeWidth={2.5} dot={{ r: 3, fill: '#4ADE80' }} />
                    <Line yAxisId="right" type="monotone" dataKey="count" name="Employees" stroke="#60A5FA" strokeWidth={2} strokeDasharray="5 3" dot={{ r: 3, fill: '#60A5FA' }} />
                  </ComposedChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 sticky top-[60px] z-20 print:hidden">
        <div className="max-w-[1720px] mx-auto px-[5%] py-3 flex flex-wrap items-center gap-2">
          {/* Pay period pickers */}
          <div className="flex items-center gap-1">
            <select
              value={pMonth}
              onChange={(e) => setPeriod(`${e.target.value}-${pYear}`)}
              className="border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:border-gray-900"
              data-testid="payroll-month"
            >
              {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select
              value={pYear}
              onChange={(e) => setPeriod(`${pMonth}-${e.target.value}`)}
              className="border border-gray-300 px-2 py-2 text-sm bg-white focus:outline-none focus:border-gray-900"
              data-testid="payroll-year"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          <select
            value={dept}
            onChange={(e) => setDept(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-900"
            data-testid="payroll-dept-filter"
          >
            <option value="">All Divisions</option>
            {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>

          <select
            value={modeFilter}
            onChange={(e) => setModeFilter(e.target.value)}
            className="border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-gray-900"
            data-testid="payroll-mode-filter"
          >
            <option value="">All Modes</option>
            {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>

          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search employee, designation, remarks…"
              className="w-full border border-gray-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-gray-900"
              data-testid="payroll-search"
            />
          </div>

          <button
            onClick={copyFromPrevious}
            disabled={copying || locked}
            title={`Copy every entry from ${prevPeriod} into ${period}`}
            className="inline-flex items-center gap-1.5 border border-gray-900 bg-gray-900 hover:bg-black text-white text-xs font-semibold px-3 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="copy-from-previous-btn"
          >
            {copying ? <Loader2 size={13} className="animate-spin" /> : <Copy size={13} />}
            Copy from {prevPeriod}
          </button>

          <button
            onClick={syncJamku}
            disabled={syncing || locked}
            title={`Fetch attendance from Jamku for ${period} and auto-fill Working/Present/Absent`}
            className="inline-flex items-center gap-1.5 border border-brand-blue bg-brand-blue hover:bg-brand-blue-hover text-white text-xs font-semibold px-3 py-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="sync-jamku-btn"
          >
            {syncing ? <Loader2 size={13} className="animate-spin" /> : <CalendarCheck size={13} />}
            {syncing ? 'Syncing…' : `Sync Jamku ${period}`}
          </button>
        </div>
      </section>

      {/* Approval workflow — moving-train tracker at the top of the month */}
      <ApprovalTracker
        period={period}
        approval={approval}
        acting={acting}
        onApprove={approveMonth}
        onUnapprove={unapproveMonth}
      />

      {/* Table */}
      <section className="py-6" ref={printRef}>
        <div className="max-w-[1720px] mx-auto px-[5%]">
          <div className="hidden print:block mb-4">
            <h2 className="text-xl font-bold">R S T &amp; Co. — Payroll Register — {period}</h2>
          </div>

          {error && !editing && (
            <div className="border border-red-300 bg-red-50 text-red-800 text-sm px-4 py-2 mb-4" data-testid="payroll-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-16 text-center text-gray-500">
              <Loader2 className="mx-auto mb-3 animate-spin" size={22} />
              Loading payroll…
            </div>
          ) : filtered.length === 0 ? (
            <div className="border-2 border-dashed border-gray-200 py-20 text-center">
              <IndianRupee size={30} className="text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm mb-4">
                {rows.length === 0 ? `No payroll entries for ${period}.` : 'No entries match current filters.'}
              </p>
              <Button onClick={openNew} className="bg-gray-900 hover:bg-black text-white rounded-none" data-testid="add-first-payroll-btn">
                <Plus size={14} className="mr-1.5" /> Add first entry
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              {entityBlocks.map((block, si) => (
                <EntityBlock
                  key={block.entity}
                  index={si}
                  block={block}
                  onEdit={openEdit}
                  onRemove={remove}
                  onAttendance={patchAttendance}
                  onInitiate={toggleInitiate}
                  onVerify={toggleVerify}
                  onPaymentMode={patchPaymentMode}
                  locked={locked}
                  acting={acting}
                />
              ))}

              {/* Grand summary — every division × entity with sub-totals */}
              <div className="border border-gray-200" data-testid="payroll-grand-summary">
                <div className="bg-gray-900 text-white px-5 py-4">
                  <div className="font-mono text-[10px] tracking-widest text-brand-yellow uppercase mb-1">
                    Grand Summary · {period}
                  </div>
                  <div className="font-heading text-lg font-bold">
                    {stats.count} entries across {entityBlocks.length} entit{entityBlocks.length === 1 ? 'y' : 'ies'}
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-[10px] font-mono tracking-widest text-gray-600 uppercase">
                    <tr>
                      <th className="text-left px-4 py-2">Entity / Division</th>
                      <th className="text-right px-4 py-2 w-24">Members</th>
                      <th className="text-right px-4 py-2 w-32">Basic</th>
                      <th className="text-right px-4 py-2 w-32">Gross</th>
                      <th className="text-right px-4 py-2 w-32">Deductions</th>
                      <th className="text-right px-4 py-2 w-32">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entityBlocks.map((blk) => (
                      <React.Fragment key={blk.entity}>
                        <tr className="bg-brand-yellow/10 border-t border-brand-yellow/30">
                          <td className="px-4 py-2 font-semibold text-gray-900">{blk.entity}</td>
                          <td className="px-4 py-2 text-right tabular-nums">{blk.count}</td>
                          <td className="px-4 py-2 text-right tabular-nums">₹ {money(blk.basic)}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-semibold">₹ {money(blk.gross)}</td>
                          <td className="px-4 py-2 text-right tabular-nums text-red-700">−₹ {money(blk.ded)}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-bold text-green-700">₹ {money(blk.net)}</td>
                        </tr>
                        {blk.subs.length > 1 ? blk.subs.map((sub) => (
                          <tr key={`${blk.entity}::${sub.title}`} className="border-b border-gray-100">
                            <td className="px-4 py-1.5 pl-10 text-gray-700 text-xs">
                              <span className="text-gray-400">↳</span>&nbsp;{sub.title}
                            </td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-xs">{sub.count}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-xs">₹ {money(sub.basic)}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-xs">₹ {money(sub.gross)}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-xs text-red-700">−₹ {money(sub.ded)}</td>
                            <td className="px-4 py-1.5 text-right tabular-nums text-xs text-green-700 font-semibold">₹ {money(sub.net)}</td>
                          </tr>
                        )) : null}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-900 text-white text-sm font-mono">
                    <tr>
                      <td className="px-4 py-3 uppercase tracking-widest text-brand-yellow font-bold">Grand Total</td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold">{stats.count}</td>
                      <td className="px-4 py-3 text-right tabular-nums">
                        ₹ {money(entityBlocks.reduce((a, s) => a + s.basic, 0))}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums font-bold">₹ {money(stats.gross)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-red-300 font-bold">−₹ {money(stats.ded)}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-green-300 font-bold">₹ {money(stats.net)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Editor drawer */}
      {editing && (
        <div className="fixed inset-0 z-40 bg-black/50 flex justify-end print:hidden" onClick={cancel} data-testid="payroll-editor">
          <div className="w-full bg-white h-full overflow-y-auto overflow-x-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="font-mono text-[9px] tracking-widest text-brand-yellow uppercase">
                  {editing.id ? 'Edit payroll entry' : 'Add payroll entry'}
                </div>
                <div className="font-heading text-xl font-bold text-gray-900">
                  {editing.employee_name || 'New Entry'} · {editing.pay_period}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={save}
                  disabled={saving || !!dupExisting}
                  title={dupExisting ? 'This employee already has an entry for this pay period.' : ''}
                  className="bg-gray-900 hover:bg-black text-white rounded-none disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="save-payroll-btn"
                >
                  {saving ? <><Loader2 size={14} className="mr-2 animate-spin" /> Saving…</> : <><Save size={14} className="mr-2" /> Save</>}
                </Button>
                <button onClick={cancel} className="p-2 border border-gray-200 hover:border-gray-900 hover:bg-gray-100" title="Close" data-testid="close-payroll-editor">
                  <X size={16} />
                </button>
              </div>
            </div>

            {error && (
              <div className="mx-6 mt-4 border border-red-300 bg-red-50 text-red-800 text-sm px-3 py-2">
                {error}
              </div>
            )}

            <div className="p-6 space-y-5">
              {/* Pay period */}
              <FieldGroup label="Pay Period (MM-YYYY)*" className="max-w-md">
                <div className="flex items-center gap-1">
                  <select
                    value={editing.pay_period.split('-')[0]}
                    onChange={(e) => handleField('pay_period', `${e.target.value}-${editing.pay_period.split('-')[1]}`)}
                    className="admin-input"
                    data-testid="editor-pay-month"
                  >
                    {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <select
                    value={editing.pay_period.split('-')[1]}
                    onChange={(e) => handleField('pay_period', `${editing.pay_period.split('-')[0]}-${e.target.value}`)}
                    className="admin-input"
                    data-testid="editor-pay-year"
                  >
                    {years.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
              </FieldGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-start">
                <FieldGroup label="Employee Name*">
                  <input
                    value={editing.employee_name}
                    onChange={(e) => onNameChange(e.target.value)}
                    className="admin-input"
                    list="payroll-employee-list"
                    placeholder="Start typing or pick from list…"
                    autoComplete="off"
                    data-testid="editor-emp-name"
                  />
                  <datalist id="payroll-employee-list">
                    {activeEmployees.map((emp) => (
                      <option key={emp.id} value={emp.name}>
                        {[emp.designation, divisionForEmployee(emp)].filter(Boolean).join(' · ')}
                      </option>
                    ))}
                  </datalist>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Pick from {activeEmployees.length} active employees — designation &amp; division auto-fill.
                  </p>
                  {dupExisting && (
                    <div
                      className="mt-2 border border-amber-300 bg-amber-50 text-amber-800 text-[11px] px-2 py-1.5 flex items-start gap-1.5"
                      data-testid="payroll-duplicate-warning"
                    >
                      <span className="font-mono font-bold">!</span>
                      <span>
                        <b>{dupExisting.employee_name}</b> already has a payroll entry for{' '}
                        <b>{dupExisting.pay_period}</b>. Edit that entry instead of creating a duplicate.
                      </span>
                    </div>
                  )}
                </FieldGroup>
                <FieldGroup label="Designation">
                  <input
                    value={editing.designation || ''}
                    onChange={(e) => handleField('designation', e.target.value)}
                    className="admin-input"
                    data-testid="editor-emp-designation"
                  />
                </FieldGroup>
                <FieldGroup label="Division">
                  <select
                    value={editing.department || ''}
                    onChange={(e) => handleField('department', e.target.value)}
                    className="admin-input"
                    data-testid="editor-emp-division"
                  >
                    <option value="">— Select division —</option>
                    {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
                    {editing.department && !divisions.includes(editing.department) && (
                      <option value={editing.department}>{editing.department} (legacy)</option>
                    )}
                  </select>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Same divisions as Team Directory. Auto-filled when you pick an employee.
                  </p>
                </FieldGroup>
                <FieldGroup label="Entity / Sub-division">
                  <input
                    value={editing.entity || ''}
                    onChange={(e) => handleField('entity', e.target.value)}
                    list="payroll-entity-list"
                    className="admin-input"
                    placeholder="e.g. RST & Co., SCCS LLP — or type new"
                    data-testid="editor-emp-entity"
                  />
                  <datalist id="payroll-entity-list">
                    {allEntities.map((v) => <option key={v} value={v} />)}
                  </datalist>
                  <p className="text-[10px] text-gray-500 mt-1">
                    {allEntities.length
                      ? `Pick from ${allEntities.length} existing entit${allEntities.length === 1 ? 'y' : 'ies'}, or type new.`
                      : 'Type to add the first entity.'}
                  </p>
                </FieldGroup>
              </div>

              {/* Earnings section */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3">
                  Earnings
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  <FieldGroup label="Basic Pay">
                    <MoneyInput
                      value={editing.basic_pay}
                      onChange={(v) => handleField('basic_pay', v)}
                      testId="editor-basic-pay"
                    />
                  </FieldGroup>
                  {ALLOWANCE_FIELDS.map(([k, label]) => (
                    <FieldGroup key={k} label={label}>
                      <MoneyInput
                        value={editing.allowances[k]}
                        onChange={(v) => handleAllowance(k, v)}
                        testId={`editor-allow-${k}`}
                      />
                    </FieldGroup>
                  ))}
                </div>
              </div>

              {/* Attendance & Leave (from Jamku) */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3">
                  Attendance &amp; Leave <span className="text-gray-400 normal-case tracking-normal">· from Jamku Attendance Analytics</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                  <FieldGroup label="Working Days">
                    <input type="number" min="0" step="1" value={editing.working_days ?? 0}
                      onChange={(e) => handleField('working_days', e.target.value)}
                      className="admin-input tabular-nums text-right" data-testid="editor-working-days" />
                  </FieldGroup>
                  <FieldGroup label="Present">
                    <input type="number" min="0" step="1" value={editing.present_days ?? 0}
                      onChange={(e) => handleField('present_days', e.target.value)}
                      className="admin-input tabular-nums text-right" data-testid="editor-present-days" />
                  </FieldGroup>
                  <FieldGroup label="Absent (Leave)">
                    <input type="number" min="0" step="1" value={editing.absent_days ?? 0}
                      onChange={(e) => handleField('absent_days', e.target.value)}
                      className="admin-input tabular-nums text-right" data-testid="editor-absent-days" />
                  </FieldGroup>
                  <FieldGroup label="Leave Salary Deduction">
                    <div className="admin-input bg-gray-50 text-right tabular-nums font-semibold text-red-700 cursor-not-allowed"
                      data-testid="editor-leave-deduction">₹ {money(leaveDeduction)}</div>
                  </FieldGroup>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  Daily salary = Gross ₹{money(editorTotals.gross)} ÷ {new Date(Number((editing.pay_period || '01-2026').split('-')[1]), Number((editing.pay_period || '01-2026').split('-')[0]), 0).getDate()} days
                  = ₹{money(editorTotals.daily)}/day. Leave deduction = Absent × daily salary, auto-added to the Deductions column &amp; Net Pay.
                </p>
              </div>


              {/* Deductions section */}
              <div className="border border-gray-200 p-4">
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mb-3">
                  Deductions
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {DEDUCTION_FIELDS.map(([k, label]) => (
                    <FieldGroup key={k} label={label}>
                      <MoneyInput
                        value={editing.deductions[k]}
                        onChange={(v) => handleDeduction(k, v)}
                        testId={`editor-ded-${k}`}
                      />
                    </FieldGroup>
                  ))}
                </div>

                {/* Salary Advance */}
                <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mt-5 mb-3 border-t border-gray-100 pt-4">
                  Salary Advance
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  <FieldGroup label="Salary Advance">
                    <MoneyInput
                      value={editing.advance_amount}
                      onChange={(v) => applyAdvance(v, editing.advance_emi_count)}
                      testId="editor-advance-amount"
                    />
                  </FieldGroup>
                  <FieldGroup label="Date of Advance">
                    <input
                      type="date"
                      value={toISODate(editing.advance_date || '')}
                      onChange={(e) => handleField('advance_date', fromISODate(e.target.value))}
                      className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:border-gray-900"
                      data-testid="editor-advance-date"
                    />
                  </FieldGroup>
                  <FieldGroup label="No. of EMI">
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={editing.advance_emi_count ?? 0}
                      onChange={(e) => applyAdvance(editing.advance_amount, e.target.value)}
                      className="w-full border border-gray-300 px-3 py-2 text-sm tabular-nums text-right focus:outline-none focus:border-gray-900"
                      data-testid="editor-advance-emi-count"
                    />
                  </FieldGroup>
                  <FieldGroup label="Balance">
                    <MoneyInput
                      value={editing.advance_balance}
                      onChange={(v) => handleField('advance_balance', v)}
                      testId="editor-advance-balance"
                    />
                  </FieldGroup>
                  <FieldGroup label="Deductions">
                    <MoneyInput
                      value={editing.deductions?.salary_advance}
                      onChange={(v) => handleDeduction('salary_advance', v)}
                      testId="editor-ded-salary_advance"
                    />
                  </FieldGroup>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  Enter the <span className="font-semibold">Salary Advance</span> and <span className="font-semibold">No. of EMI</span> — the monthly <span className="font-semibold">Deductions</span> auto-calculates (Advance ÷ EMIs) and the <span className="font-semibold">Balance</span> updates. The Deductions amount is recovered this period and added to the Deductions column. Fine-tune Balance/Deductions in the final month so the balance closes at zero.
                </p>
              </div>

              {/* Payment */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <FieldGroup label="Payment Mode">
                  <select
                    value={editing.payment_mode || 'RTGS/NEFT'}
                    onChange={(e) => handleField('payment_mode', e.target.value)}
                    className="admin-input"
                    data-testid="editor-payment-mode"
                  >
                    {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Inst. No. / Reference">
                  <input
                    value={editing.payment_ref || ''}
                    onChange={(e) => handleField('payment_ref', e.target.value)}
                    placeholder={
                      editing.payment_mode === 'Cheque' ? 'Cheque number' :
                      editing.payment_mode === 'RTGS/NEFT' ? 'RTGS / NEFT ref' :
                      'Voucher / Receipt no.'
                    }
                    className="admin-input font-mono"
                    data-testid="editor-payment-ref"
                  />
                </FieldGroup>
                <FieldGroup label="Payment Date">
                  <input
                    type="date"
                    value={toISODate(editing.payment_date)}
                    onChange={(e) => handleField('payment_date', fromISODate(e.target.value))}
                    className="admin-input"
                    data-testid="editor-payment-date"
                  />
                </FieldGroup>
              </div>

              <FieldGroup label="Remarks">
                <textarea
                  value={editing.remarks || ''}
                  onChange={(e) => handleField('remarks', e.target.value)}
                  placeholder="Bonus, arrears, leave encashment, etc."
                  className="admin-input min-h-[72px]"
                  data-testid="editor-remarks"
                />
              </FieldGroup>

              {/* Live totals */}
              <div className="bg-gray-900 text-white p-4">
                <div className="font-mono text-[10px] tracking-widest text-white/60 uppercase mb-3">
                  Computed Totals
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="font-mono text-[10px] tracking-widest text-brand-yellow uppercase mb-1">Gross</div>
                    <div className="font-heading text-2xl font-black tabular-nums" data-testid="editor-total-gross">₹ {money(editorTotals.gross)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-widest text-red-300 uppercase mb-1">Deductions</div>
                    <div className="font-heading text-2xl font-black tabular-nums text-red-300">−₹ {money(editorTotals.ded)}</div>
                  </div>
                  <div>
                    <div className="font-mono text-[10px] tracking-widest text-green-300 uppercase mb-1">Net Pay</div>
                    <div className="font-heading text-2xl font-black tabular-nums text-green-300" data-testid="editor-total-net">₹ {money(editorTotals.net)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-input {
          width: 100%;
          border: 1px solid #d1d5db;
          padding: 8px 10px;
          font-size: 14px;
          outline: none;
          background: white;
        }
        .admin-input:focus { border-color: #111827; }
        @keyframes trainbob { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-3px); } }
        .payroll-train { animation: trainbob 1.6s ease-in-out infinite; }
      `}</style>
    </div>
  );
};

const AttNum = ({ value, onCommit, testId, disabled }) => {
  const [v, setV] = useState(value ?? 0);
  useEffect(() => { setV(value ?? 0); }, [value]);
  return (
    <input
      type="number" min="0" step="1" value={v} disabled={disabled}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => { if (String(v) !== String(value ?? 0)) onCommit(Number(v) || 0); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      className="w-14 border border-gray-200 px-1.5 py-0.5 text-xs text-right tabular-nums focus:outline-none focus:border-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
      data-testid={testId}
    />
  );
};

const EntityBlock = ({ index, block, onEdit, onRemove, onAttendance, onInitiate, onVerify, onPaymentMode, locked, acting }) => {
  const accent = block.isOthers ? 'text-gray-500' : (index === 0 ? 'text-brand-yellow' : 'text-brand-blue');
  const totalsBg = 'bg-gray-50 border-t border-gray-200';

  const sumBasic = (list) => list.reduce((a, r) => a + (Number(r.basic_pay) || 0), 0);
  const sumAllow = (list) => list.reduce((a, r) => a + Object.values(r.allowances || {}).reduce((x, v) => x + (Number(v) || 0), 0), 0);
  const sumGross = (list) => list.reduce((a, r) => a + (Number(r.gross_earnings) || 0), 0);
  const sumNet   = (list) => list.reduce((a, r) => a + (Number(r.net_pay) || 0), 0);
  const sumDed   = (list) => sumGross(list) - sumNet(list);

  return (
    <section data-testid={`payroll-entity-${block.entity.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
      {/* Entity header */}
      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className={`font-mono text-[10px] tracking-[0.25em] uppercase mb-1 ${accent}`}>
            {String(index + 1).padStart(2, '0')} · {block.count} entr{block.count === 1 ? 'y' : 'ies'}
          </div>
          <h2 className="font-heading text-xl md:text-2xl font-black tracking-tight text-gray-900">
            {block.entity}
          </h2>
        </div>
        <div className="flex gap-6 text-right">
          <div>
            <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase">Gross</div>
            <div className="font-heading text-lg font-bold tabular-nums text-gray-900">₹ {money(block.gross)}</div>
          </div>
          <div>
            <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase">Net</div>
            <div className="font-heading text-lg font-bold tabular-nums text-green-700">₹ {money(block.net)}</div>
          </div>
        </div>
      </div>

      {/* Section table */}
      <div className="border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-900 text-white text-[10px] font-mono tracking-widest uppercase sticky top-[128px] z-10 shadow-[0_2px_4px_-1px_rgba(0,0,0,0.35)]">
            <tr>
              <th className="text-left px-3 py-3">Employee</th>
              <th className="text-left px-3 py-3 hidden md:table-cell">Designation</th>
              <th className="text-left px-3 py-3 hidden lg:table-cell">Entity</th>
              <th className="text-center px-2 py-3 hidden md:table-cell" title="Working Days">WD</th>
              <th className="text-center px-2 py-3 hidden md:table-cell">Present</th>
              <th className="text-center px-2 py-3 hidden md:table-cell">Absent</th>
              <th className="text-right px-3 py-3">Basic</th>
              <th className="text-right px-3 py-3 hidden md:table-cell">Allow.</th>
              <th className="text-right px-3 py-3">Gross</th>
              <th className="text-right px-3 py-3 hidden md:table-cell">Ded.</th>
              <th className="text-right px-3 py-3">Net</th>
              <th className="text-left px-3 py-3 hidden xl:table-cell">Mode</th>
              <th className="text-left px-3 py-3 hidden xl:table-cell">Remarks</th>
              <th className="text-center px-3 py-3 w-48 print:hidden">Approval</th>
            </tr>
          </thead>
          {block.subs.map((sub, gi) => (
            <tbody key={sub.title || `__sub_${gi}`} className="border-b-[3px] border-gray-200 last:border-b-0">
              <tr className="bg-brand-yellow/10 border-t border-brand-yellow/30">
                <td colSpan={14} className="px-3 py-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-mono text-[10px] tracking-widest uppercase text-gray-900 font-semibold">
                      → {sub.title} · {sub.rows.length} {sub.rows.length === 1 ? 'entry' : 'entries'}
                    </div>
                    <div className="text-[11px] font-mono text-gray-700">
                      <span className="text-gray-500">Gross</span>&nbsp;<span className="font-bold tabular-nums">₹ {money(sumGross(sub.rows))}</span>
                      <span className="mx-2 text-gray-300">|</span>
                      <span className="text-gray-500">Net</span>&nbsp;<span className="font-bold tabular-nums text-green-700">₹ {money(sumNet(sub.rows))}</span>
                    </div>
                  </div>
                </td>
              </tr>
              {sub.rows.map((r) => {
                const allowSum = Object.values(r.allowances || {}).reduce((a, v) => a + (Number(v) || 0), 0);
                const dedSum   = (Number(r.gross_earnings) || 0) - (Number(r.net_pay) || 0);
                return (
                  <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50/70 align-middle" data-testid={`payroll-row-${r.id}`}>
                    <td className="px-3 py-1 leading-tight">
                      <div className="font-medium text-gray-900">{r.employee_name}</div>
                      <div className="text-[10px] text-gray-500 md:hidden">{r.designation || ''}</div>
                    </td>
                    <td className="px-3 py-1 hidden md:table-cell text-gray-700">{r.designation || '—'}</td>
                    <td className="px-3 py-1 hidden lg:table-cell text-gray-700">{r.entity || <span className="text-gray-400 italic">—</span>}</td>
                    <td className="px-2 py-1 text-center hidden md:table-cell tabular-nums text-gray-700" data-testid={`att-wd-${r.id}`}>{r.working_days ?? 0}</td>
                    <td className="px-2 py-1 text-center hidden md:table-cell tabular-nums text-gray-700" data-testid={`att-present-${r.id}`}>{r.present_days ?? 0}</td>
                    <td className="px-2 py-1 text-center hidden md:table-cell tabular-nums text-gray-700" data-testid={`att-absent-${r.id}`}>{r.absent_days ?? 0}</td>
                    <td className="px-3 py-1 text-right tabular-nums text-gray-800">{money(r.basic_pay)}</td>
                    <td className="px-3 py-1 text-right tabular-nums text-gray-700 hidden md:table-cell">{money(allowSum)}</td>
                    <td className="px-3 py-1 text-right tabular-nums font-semibold text-gray-900">{money(r.gross_earnings)}</td>
                    <td className="px-3 py-1 text-right tabular-nums text-red-700 hidden md:table-cell">−{money(dedSum)}</td>
                    <td className="px-3 py-1 text-right tabular-nums font-semibold text-green-700">{money(r.net_pay)}</td>
                    <td className="px-3 py-1 hidden xl:table-cell text-gray-700 text-xs leading-tight">
                      <select
                        value={PAYMENT_MODES.includes(r.payment_mode) ? r.payment_mode : 'RTGS/NEFT'}
                        onChange={(e) => onPaymentMode(r, e.target.value)}
                        disabled={locked || acting}
                        className="border border-gray-200 px-1 py-0.5 text-xs bg-white focus:outline-none focus:border-gray-900 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        data-testid={`payment-mode-${r.id}`}
                      >
                        {PAYMENT_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
                      </select>
                      {r.payment_ref && <span className="text-gray-400"> · #{r.payment_ref}</span>}
                      {r.payment_date && <span className="text-[10px] text-gray-400"> · {r.payment_date}</span>}
                    </td>
                    <td className="px-3 py-1 hidden xl:table-cell text-gray-600 text-xs max-w-[220px] truncate" title={r.remarks || ''}>{r.remarks || ''}</td>
                    <td className="px-3 py-1 print:hidden">
                      <div className="flex items-center gap-1 justify-end flex-wrap">
                        <button
                          onClick={() => onInitiate(r)}
                          disabled={locked || acting}
                          title={r.initiated_at ? `Initiated by ${r.initiated_by || '—'} · ${fmtStamp(r.initiated_at)}` : 'Initiate (Accountant)'}
                          className={`inline-flex items-center gap-1 px-1.5 py-1 border text-[10px] font-mono font-semibold uppercase tracking-wide transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${r.initiated_at ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'border-gray-200 text-gray-500 hover:border-gray-900'}`}
                          data-testid={`initiate-payroll-${r.id}`}
                        >
                          {r.initiated_at ? <Check size={11} /> : <Circle size={11} />} I
                        </button>
                        <button
                          onClick={() => onVerify(r)}
                          disabled={locked || acting || !r.initiated_at}
                          title={!r.initiated_at ? 'Must be Initiated first' : (r.verified_at ? `Verified by ${r.verified_by || '—'} · ${fmtStamp(r.verified_at)}` : 'Verify (Manager)')}
                          className={`inline-flex items-center gap-1 px-1.5 py-1 border text-[10px] font-mono font-semibold uppercase tracking-wide transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${r.verified_at ? 'bg-sky-50 border-sky-400 text-sky-700' : 'border-gray-200 text-gray-500 hover:border-gray-900'}`}
                          data-testid={`verify-payroll-${r.id}`}
                        >
                          {r.verified_at ? <Check size={11} /> : <Circle size={11} />} V
                        </button>
                        <button onClick={() => onEdit(r)} disabled={locked} className="p-1 border border-gray-200 hover:border-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={locked ? 'Month is locked' : 'Edit'} data-testid={`edit-payroll-${r.id}`}>
                          <Pencil size={12} />
                        </button>
                        <button onClick={() => onRemove(r)} disabled={locked} className="p-1 border border-red-100 text-red-600 hover:border-red-500 hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={locked ? 'Month is locked' : 'Delete'} data-testid={`delete-payroll-${r.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              <tr className="bg-gray-50 text-[11px] font-mono">
                <td className="px-3 py-1.5 uppercase tracking-widest text-gray-600" colSpan={3}>
                  Subtotal · {sub.title}
                </td>
                <td className="px-3 py-1.5 text-right tabular-nums">₹ {money(sumBasic(sub.rows))}</td>
                <td className="px-3 py-1.5 text-right tabular-nums hidden md:table-cell">₹ {money(sumAllow(sub.rows))}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-semibold">₹ {money(sumGross(sub.rows))}</td>
                <td className="px-3 py-1.5 text-right tabular-nums text-red-700 hidden md:table-cell">−₹ {money(sumDed(sub.rows))}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-bold text-green-700">₹ {money(sumNet(sub.rows))}</td>
                <td className="hidden xl:table-cell"></td>
                <td className="hidden xl:table-cell"></td>
                <td className="print:hidden"></td>
              </tr>
            </tbody>
          ))}
          <tfoot className={`${totalsBg} text-xs font-mono`}>
            <tr>
              <td className="px-3 py-2 uppercase tracking-widest text-gray-600 font-semibold" colSpan={3}>
                {block.entity} total · {block.count} {block.count === 1 ? 'entry' : 'entries'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums">₹ {money(sumBasic(block.rows))}</td>
              <td className="px-3 py-2 text-right tabular-nums hidden md:table-cell">₹ {money(sumAllow(block.rows))}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold">₹ {money(block.gross)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-red-700 hidden md:table-cell">−₹ {money(block.ded)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-bold text-green-700">₹ {money(block.net)}</td>
              <td className="hidden xl:table-cell"></td>
              <td className="hidden xl:table-cell"></td>
              <td className="print:hidden"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  );
};

const MoneyInput = ({ value, onChange, testId }) => (
  <div className="relative">
    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">₹</span>
    <input
      type="number"
      min="0"
      step="0.01"
      value={value ?? 0}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 pl-6 pr-2 py-2 text-sm tabular-nums text-right focus:outline-none focus:border-gray-900"
      data-testid={testId}
    />
  </div>
);

const KPI = ({ label, value, icon, tone }) => {
  const toneMap = {
    yellow: 'text-brand-yellow',
    green:  'text-green-400',
    amber:  'text-amber-400',
    mono:   'text-white',
  };
  return (
    <div className="bg-[#0a0f1e] p-4">
      <div className="flex items-center gap-2 mb-1 text-white/60">
        {icon}
        <span className="font-mono text-[10px] tracking-widest uppercase">{label}</span>
      </div>
      <div className={`font-heading text-xl md:text-2xl font-black tabular-nums ${toneMap[tone] || toneMap.mono}`}>{value}</div>
    </div>
  );
};

// Moving-train approval tracker — sits at the top of the month's payroll.
// Three stations: Accountant Initiated → Manager Verified → Partner Approved.
const ApprovalTracker = ({ period, approval, acting, onApprove, onUnapprove }) => {
  if (!approval) return null;
  const total = approval.total || 0;
  const roles = approval.roles || {};
  const S = approval.stages || {};
  const ini = S.initiated || {}, ver = S.verified || {}, app = S.approved || {};

  const f1 = total ? (ini.count || 0) / total : 0;
  const f2 = total ? (ver.count || 0) / total : 0;
  const f3 = app.complete ? 1 : 0;
  const overall = total ? (f1 + f2 + f3) / 3 : 0;

  const stations = [
    { key: 'ini', n: 1, title: roles.accountant?.stage || 'Accountant Initiated', who: roles.accountant?.name, role: 'Accountant', count: ini.count || 0, complete: !!ini.complete, at: ini.at, started: (ini.count || 0) > 0 },
    { key: 'ver', n: 2, title: roles.manager?.stage || 'Manager Verified', who: roles.manager?.name, role: 'Manager', count: ver.count || 0, complete: !!ver.complete, at: ver.at, started: (ver.count || 0) > 0 },
    { key: 'app', n: 3, title: roles.partner?.stage || 'Partner Approved', who: roles.partner?.name, role: 'Partner', complete: !!app.complete, at: app.at, by: app.by, single: true },
  ];
  const nodeState = (s) =>
    s.complete ? 'done'
      : (s.started || (s.key === 'app' && approval.can_approve)) ? 'active'
      : 'pending';

  return (
    <section className="bg-white border-b border-gray-200 print:hidden" data-testid="payroll-approval-tracker">
      <div className="max-w-[1720px] mx-auto px-[5%] py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="font-mono text-[10px] tracking-[0.25em] text-gray-500 uppercase">
            Approval Workflow · {periodLabel(period)} · {total} employee{total === 1 ? '' : 's'}
          </div>
          {approval.approved && (
            <div className="inline-flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-300 text-emerald-700 text-xs font-semibold px-3 py-1.5" data-testid="payroll-locked-badge">
                <Lock size={13} /> Locked · Partner-approved{app.at ? ` · ${fmtStamp(app.at)}` : ''}
              </span>
              <button onClick={() => downloadPayrollPdf(period, 'RST & Co.')} className="inline-flex items-center gap-1.5 bg-gray-900 hover:bg-black text-white text-xs font-semibold px-3 py-1.5 transition-colors" data-testid="download-rst-pdf-btn">
                <Printer size={13} /> RST &amp; Co. PDF
              </button>
              <button onClick={() => downloadPayrollPdf(period, 'SCCS LLP')} className="inline-flex items-center gap-1.5 bg-brand-blue hover:bg-brand-blue-hover text-white text-xs font-semibold px-3 py-1.5 transition-colors" data-testid="download-sccs-pdf-btn">
                <Printer size={13} /> SCCS LLP PDF
              </button>
              <button onClick={onUnapprove} disabled={acting} className="inline-flex items-center gap-1.5 border border-gray-300 text-gray-600 hover:border-gray-900 text-xs font-semibold px-3 py-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-testid="payroll-unlock-btn">
                <Unlock size={13} /> Unlock
              </button>
            </div>
          )}
        </div>

        {total === 0 ? (
          <div className="text-sm text-gray-400 py-4">No payroll entries for this month yet — add entries to begin the approval flow.</div>
        ) : (
          <div className="relative pt-8 pb-2">
            <div className="absolute left-[8%] right-[8%] top-[46px] h-[3px] bg-gray-200 rounded-full" />
            <div className="absolute left-[8%] top-[46px] h-[3px] bg-gradient-to-r from-emerald-400 via-sky-400 to-brand-yellow rounded-full transition-[width] duration-700 ease-out" style={{ width: `calc(${overall} * 84%)` }} />
            <div className="absolute top-[30px] transition-[left] duration-700 ease-out" style={{ left: `calc(8% + ${overall} * 84% - 12px)` }}>
              <span className="payroll-train inline-flex text-brand-blue"><TrainFront size={24} /></span>
            </div>
            <div className="relative flex justify-between">
              {stations.map((s) => {
                const st = nodeState(s);
                const ring = st === 'done'
                  ? 'bg-emerald-500 border-emerald-500 text-white'
                  : st === 'active'
                    ? 'bg-white border-brand-yellow text-gray-900 ring-4 ring-brand-yellow/20'
                    : 'bg-white border-gray-300 text-gray-400';
                return (
                  <div key={s.key} className="flex flex-col items-center text-center w-1/3 px-2" data-testid={`payroll-stage-${s.key}`}>
                    <div className={`w-9 h-9 rounded-full border-2 flex items-center justify-center font-mono text-sm font-bold ${ring}`}>
                      {st === 'done' ? <Check size={16} /> : s.n}
                    </div>
                    <div className="mt-2 font-heading text-sm font-bold text-gray-900">{s.title}</div>
                    <div className="font-mono text-[10px] tracking-widest text-gray-500 uppercase mt-0.5">{s.role} · {s.who}</div>
                    {!s.single ? (
                      <div className="text-[11px] mt-1 text-gray-600 tabular-nums">
                        {s.count}/{total} done
                        {s.complete && s.at && <span className="text-emerald-600"> · {fmtStamp(s.at)}</span>}
                      </div>
                    ) : (
                      <div className="mt-2">
                        {s.complete ? (
                          <div className="text-[11px] text-emerald-700 font-semibold">
                            Approved by {s.by || s.who}{s.at ? ` · ${fmtStamp(s.at)}` : ''}
                          </div>
                        ) : (
                          <button
                            onClick={onApprove}
                            disabled={acting || !approval.can_approve}
                            title={approval.can_approve ? 'Partner approve & lock the month' : 'All rows must be Initiated & Verified first'}
                            className="inline-flex items-center gap-1.5 bg-gray-900 hover:bg-black text-white text-xs font-semibold px-4 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="approve-month-btn"
                          >
                            <ShieldCheck size={14} /> Approve
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};


export default PayrollPage;
